import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Alert, Button, Checkbox, Modal, message } from "antd";
import {useLocation,useParams,useSearchParams} from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import AppLayoutDoctor from "../../../navigation/Layout/LayoutDoctor";
import { fetchUserById } from "../../../Profile/redux/thunks/userThunks";
import { fetchMedicalInfoByUserId } from "../../../Profile/redux/thunks/medicalThunks";
import { fetchBmiHistoryByUserId } from "../../../Profile/redux/thunks/bmiHistoryThunks";
import { fetchPersonalHistoryByUserId } from "../../../Profile/redux/thunks/personalHistoryThunks";
import { fetchVaccinations } from "../../../Profile/redux/thunks/vaccinationThunks";
import { fetchSurgicalHistory } from "../../../Profile/redux/thunks/surgicalHistoryThunks";
import { fetchFamilyHistory } from "../../../Profile/redux/thunks/familyHistoryThunks";
import { fetchEmergencyContacts } from "../../../Profile/redux/thunks/emergencyContactThunks";
import { fetchConsultationsByPatientId } from "../../redux/thunks/consultationThunks";
import { selectSelectedConsultation } from "../../redux/selectors/consultationSelectors";
import { selectLocalPatientById } from "../../../WaitingRoom/redux/selectors/localPatient.selectors";
import PatientProfileCard from "../../components/FilePage/PatientProfileCard/PatientProfileCard";
import NephrologyProfilePanel from "../../components/FilePage/NephrologyProfilePanel/NephrologyProfilePanel";
import ConsultationTabs from "../../components/FilePage/ConsultationTabs/ConsultationTabs";
import AddNewConsultationPopup from "../../components/AddNewConsultationPopup/AddNewConsultation/AddNewConsultation";
import NephrologueConsultationPage from "../../../specialty/neurologue/pages/NeurologueConsultationPage/NeurologueConsultationPage";
import PulmonologyConsultationPage from "../../../specialty/pulmonology/pages/PulmonologyConsultationPage/PulmonologyConsultationPage";

import { getSharedDocsStorageKey } from "../../../medicalRecord/utils/storageKeyUtils";
import { patientRecordDashboardFallbacks } from "../../fakedata/patientRecordDashboardFallbacks";
import useMedicalCapabilities, {
  MEDICAL_ACCESS_SCOPE_OPTIONS,
} from "../../../authorization/useMedicalCapabilities";
import { axiosRDV } from "../../../api/axios";

import "./File.css";

/* =========================================================
   DEFAULTS
========================================================= */

const DEFAULT_PATIENT = {
  id: null,
  patientId: null,
  photo: null,
  name: "—",
  phone: null,
  email: null,
  gender: null,
  bloodType: null,
  height: null,
  weight: null,
  diseases: null,
  latestVisit: null,
};

const DEFAULT_CONSULTATION_ACCESS_SCOPES = [
  "medical_information:read",
  "medical_information:create",
  "medical_document:read",
  "medication:read",
];

const DEFAULT_EMERGENCY = {
  name: "—",
  phone: "—",
  relation: null,
  allContacts: [],
};

const normalizeId = (
  value
) =>
  value == null
    ? ""
    : String(
        value
      ).trim();

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value == null) return [];
  return [value];
};

export default function File() {
  const { t } = useTranslation("neurologue");
  const location =
    useLocation();
  const [searchParams] = useSearchParams();

  const {
    id:
      routeUserId,
  } =
    useParams();

  const dispatch =
    useDispatch();

  const rawRouteUserId =
    normalizeId(
      routeUserId
    );

  const normalizedRouteUserId =
    rawRouteUserId.toLowerCase();
  const routePrincipalUserId = normalizeId(searchParams.get("patientId"));
  const routeSubprofileId = normalizeId(searchParams.get("subprofileId"));

  /* =======================================================
     POPUP
  ======================================================= */

  const [
    isConsultationPopupOpen,
    setIsConsultationPopupOpen,
  ] =
    useState(false);

  const [
    createdConsultationId,
    setCreatedConsultationId,
  ] =
    useState(null);

  const [
    consultationDocumentDraft,
    setConsultationDocumentDraft,
  ] =
    useState(null);

  const [
    sharedFilesForCard,
    setSharedFilesForCard,
  ] =
    useState([]);

  const [
    isNephrologyProfileOpen,
    setIsNephrologyProfileOpen,
  ] = useState(false);

  const [
    selectedSpecialty,
    setSelectedSpecialty,
  ] = useState(() => {
    const requestedSpecialty = searchParams.get("specialty");
    if (requestedSpecialty === "neurologue") return "nephrologue";
    return ["general", "nephrologue", "pulmonology"].includes(requestedSpecialty)
      ? requestedSpecialty
      : "general";
  });

  /* =======================================================
     PATIENT
  ======================================================= */

  const user =
    useSelector(
      (state) =>
        state.user
          ?.usersById?.[
          normalizedRouteUserId
        ] ||
        state.user
          ?.usersById?.[
          rawRouteUserId
        ] ||
        null
    );

  const localPatient =
    useSelector(
      selectLocalPatientById(
        rawRouteUserId
      )
    );

  const patientUser =
    user ||
    localPatient ||
    null;

  const canonicalPatientUserId =
    useMemo(
      () =>
        normalizeId(
          patientUser?.id ||
            patientUser?.userId ||
            patientUser?._id ||
            rawRouteUserId
        ),
      [
        patientUser,
        rawRouteUserId,
      ]
    );

  const lookupId =
    canonicalPatientUserId.toLowerCase();

  const patientScope = useMemo(
    () => ({
      userId: canonicalPatientUserId,
      principalUserId:
        routePrincipalUserId ||
        patientUser?.patientId ||
        patientUser?.linkedUserId ||
        canonicalPatientUserId,
      subprofileId:
        routeSubprofileId ||
        patientUser?.subprofileId ||
        patientUser?.subProfileId ||
        null,
    }),
    [canonicalPatientUserId, patientUser, routePrincipalUserId, routeSubprofileId]
  );

  /* =======================================================
     DOCTOR

     Aucun appel API ici.
  ======================================================= */

  const currentDoctor =
    useSelector(
      (state) =>
        state.doctor?.user ||
        null
    );

  const doctorId =
    normalizeId(
      currentDoctor?.id ||
        currentDoctor?._id ||
        currentDoctor?.userId ||
        currentDoctor?.doctorId
    );

  const {
    capabilities: medicalAccessCapabilities,
    status: medicalAccessStatus,
    loading: medicalAccessLoading,
    refresh: refreshMedicalCapabilities,
  } = useMedicalCapabilities(
    patientScope.principalUserId,
    doctorId,
    patientScope.subprofileId
  );
  const canReadMedicalInformation = Boolean(
    medicalAccessStatus === "local-owner" ||
      medicalAccessCapabilities?.medicalInformation?.read
  );
  const canCreateMedicalInformation = Boolean(
    medicalAccessStatus === "local-owner" ||
      medicalAccessCapabilities?.medicalInformation?.create
  );
  const canReadMedications = Boolean(
    medicalAccessStatus === "local-owner" ||
      medicalAccessCapabilities?.medications?.read
  );
  const canReadMedicalDocuments = Boolean(
    medicalAccessStatus === "local-owner" ||
      medicalAccessCapabilities?.medicalDocuments?.read
  );
  const [authorizationDialogOpen, setAuthorizationDialogOpen] = useState(false);
  const [requestedAccessScopes, setRequestedAccessScopes] = useState(
    DEFAULT_CONSULTATION_ACCESS_SCOPES
  );
  const [requestingAccess, setRequestingAccess] = useState(false);

  const sendMedicalAccessRequest = async (scopes) => {
    if (requestingAccess || !patientScope.principalUserId || !doctorId) return false;
    if (!scopes.length) {
      message.error("Select at least one permission to request.");
      return false;
    }
    setRequestingAccess(true);
    try {
      await axiosRDV.post(
        "/access-requests",
        {
          patientId: patientScope.principalUserId,
          ...(patientScope.subprofileId
            ? { subprofileId: patientScope.subprofileId }
            : {}),
          scopes,
        },
        { allowedRequestFields: ["patientId", "subprofileId"] },
      );
      message.success(
        scopes.includes("medical_information:create")
          ? "Consultation access request sent. The patient must approve it before you can save a consultation."
          : "Access request sent to the patient.",
      );
      await refreshMedicalCapabilities();
      return true;
    } catch (error) {
      console.error("[File] Erreur demande d'autorisation:", error);
      message.error(
        error?.response?.data?.message || "Unable to request patient authorization.",
      );
      return false;
    } finally {
      setRequestingAccess(false);
    }
  };

  const handleRequestMedicalAccess = async () => {
    if (await sendMedicalAccessRequest(requestedAccessScopes)) {
      setAuthorizationDialogOpen(false);
    }
  };

  const handleRequestConsultationWriteAccess = () =>
    sendMedicalAccessRequest(["medical_information:create"]);

  /* =======================================================
     CONSULTATIONS
  ======================================================= */

  const patientConsultations =
    useSelector(
      (state) =>
        state.consultation
          ?.patientConsultations ||
        []
    );

  const selectedConsultation =
    useSelector(
      selectSelectedConsultation
    );

  const consultationsForHistory =
    useMemo(() => {
      if (patientConsultations.length > 0) {
        return patientConsultations;
      }

      const selectedPatientId = normalizeId(
        selectedConsultation?.userId ||
          selectedConsultation?.patientId
      );

      return selectedConsultation &&
        selectedPatientId === canonicalPatientUserId
        ? [selectedConsultation]
        : [];
    }, [
      patientConsultations,
      selectedConsultation,
      canonicalPatientUserId,
    ]);

  /* =======================================================
     MEDICAL
  ======================================================= */

  const medical =
    useSelector(
      (state) =>
        state.medical
          ?.byId?.[
          lookupId
        ] ||
        state.medical
          ?.byId?.[
          canonicalPatientUserId
        ] ||
        state.medical
          ?.byUserId?.[
          lookupId
        ] ||
        state.medical
          ?.byUserId?.[
          canonicalPatientUserId
        ] ||
        null
    );

  if (import.meta.env.DEV) {
    console.debug("[Consultation patient medical]", {
      patientId: canonicalPatientUserId,
      insurance: medical?.insurance,
    });
  }

  const bmiHistory = normalizeArray(
    useSelector(
      (state) =>
        state.bmiHistory
          ?.byUserId?.[
          lookupId
        ] ||
        state.bmiHistory
          ?.byUserId?.[
          canonicalPatientUserId
        ] ||
        state.bmiHistory?.data ||
        []
    )
  ).filter((record) => {
    const recordUserId = normalizeId(record?.userId);
    const recordSubprofileId = normalizeId(record?.subprofileId);
    const expectedUserId = patientScope.subprofileId
      ? patientScope.principalUserId
      : canonicalPatientUserId;
    return (
      recordUserId === normalizeId(expectedUserId) &&
      recordSubprofileId === normalizeId(patientScope.subprofileId)
    );
  });

  const personalHistory =
    normalizeArray(
      useSelector(
        (state) =>
          state.personalHistory
            ?.byUserId?.[
            lookupId
          ] ||
          []
      )
    );

  const vaccinations =
    normalizeArray(
      useSelector(
        (state) =>
          state.vaccination
            ?.byUserId?.[
            lookupId
          ] ||
          []
      )
    );

  const surgicalHistory =
    normalizeArray(
      useSelector(
        (state) =>
          state.surgicalHistory
            ?.byUserId?.[
            lookupId
          ] ||
          []
      )
    );

  const familyHistory =
    normalizeArray(
      useSelector(
        (state) =>
          state.familyHistory
            ?.byUserId?.[
            lookupId
          ] ||
          []
      )
    );

  const emergencyContacts =
    normalizeArray(
      useSelector(
        (state) =>
          state.emergencyContact
            ?.byUserId?.[
            lookupId
          ] ||
          []
      )
    );

  const latestBmi =
    bmiHistory[
      bmiHistory.length -
        1
    ] ||
    null;

  /* =======================================================
     LOAD PATIENT
  ======================================================= */

  useEffect(() => {
    if (
      !rawRouteUserId
    ) {
      return;
    }

    dispatch(
      fetchUserById({
        userId: rawRouteUserId,
        principalUserId: patientScope.principalUserId,
        subprofileId: patientScope.subprofileId,
      })
    );
  }, [
    dispatch,
    rawRouteUserId,
    patientScope,
  ]);

  /* =======================================================
     LOAD MEDICAL DATA
  ======================================================= */

  useEffect(() => {
    if (!canonicalPatientUserId || medicalAccessLoading || !canReadMedicalInformation) {
      return;
    }

    dispatch(
      fetchMedicalInfoByUserId(patientScope)
    );

    dispatch(
      fetchBmiHistoryByUserId(
        canonicalPatientUserId
      )
    );

    dispatch(
      fetchPersonalHistoryByUserId(patientScope)
    );

    dispatch(
      fetchVaccinations(patientScope)
    );

    dispatch(
      fetchSurgicalHistory(
        patientScope
      )
    );

    dispatch(
      fetchFamilyHistory(
        patientScope
      )
    );

    dispatch(
      fetchEmergencyContacts({
        userId:
          patientScope.principalUserId,
        ...(patientScope.subprofileId
          ? { subprofileId: patientScope.subprofileId }
          : {}),
      })
    );
  }, [
    dispatch,
    canonicalPatientUserId,
    patientScope,
    medicalAccessLoading,
    canReadMedicalInformation,
  ]);

  /* =======================================================
     CONSULTATIONS REFRESH
  ======================================================= */

  const refreshConsultations =
    useCallback(
      async () => {
        if (
          !canonicalPatientUserId ||
          medicalAccessLoading ||
          !canReadMedicalInformation
        ) {
          return null;
        }

        console.log(
          "🔄 FETCH CONSULTATIONS",
          {
            userId:
              canonicalPatientUserId,

            doctorId,
          }
        );

        return dispatch(
          fetchConsultationsByPatientId({
            userId: patientScope.principalUserId,

            doctorId,
            subprofileId: patientScope.subprofileId,
          })
        );
      },
      [
        dispatch,
        canonicalPatientUserId,
        patientScope,
        doctorId,
        medicalAccessLoading,
        canReadMedicalInformation,
      ]
    );

  useEffect(() => {
    refreshConsultations();
  }, [
    refreshConsultations,
  ]);

  /* =======================================================
     SHARED DOCS
  ======================================================= */

  useEffect(() => {
    if (
      !canonicalPatientUserId
    ) {
      return;
    }

    const load =
      () => {
        try {
          const key =
            getSharedDocsStorageKey(
              canonicalPatientUserId
            );

          const value =
            JSON.parse(
              localStorage.getItem(
                key
              ) ||
                "[]"
            );

          setSharedFilesForCard(
            Array.isArray(
              value
            )
              ? value
              : []
          );
        } catch (
          error
        ) {
          console.error(
            "Shared docs:",
            error
          );

          setSharedFilesForCard(
            []
          );
        }
      };

    load();

    window.addEventListener(
      "focus",
      load
    );

    window.addEventListener(
      "storage",
      load
    );

    return () => {
      window.removeEventListener(
        "focus",
        load
      );

      window.removeEventListener(
        "storage",
        load
      );
    };
  }, [
    canonicalPatientUserId,
  ]);

  /* =======================================================
     PATIENT DATA
  ======================================================= */

  const patientData =
    useMemo(() => {
      if (!patientUser) {
        return (
          patientRecordDashboardFallbacks
            ?.patient ||
          DEFAULT_PATIENT
        );
      }

      const name =
        `${patientUser.firstName || ""} ${patientUser.lastName || ""}`.trim() ||
        patientUser.name ||
        "—";

      return {
        id:
          canonicalPatientUserId,

        userId:
          canonicalPatientUserId,

        isLocalPatient: Boolean(localPatient),

        patientId:
          patientUser.identifier ||
          patientUser.patientId ||
          null,

        identifier:
          patientUser.identifier ||
          patientUser.patientIdentifier ||
          null,

        photo:
          patientUser.avatarUrl ||
          patientUser.avatar ||
          null,

        name,

        phone:
          patientUser.phone ||
          null,

        email:
          patientUser.email ||
          null,

        gender:
          patientUser.gender ||
          null,

        birthdate:
          patientUser.birthdate ||
          patientUser.dateOfBirth ||
          null,

        bloodType:
          medical
            ?.groupeSanguin ||
          medical?.bloodType ||
          null,

        height:
          medical?.tailleCm ??
          latestBmi?.tailleCm ??
          medical?.height ??
          null,

        weight:
          medical?.poidsKg ??
          latestBmi?.poidsKg ??
          medical?.weight ??
          null,

        diseases:
          medical?.diseases ||
          null,

        latestVisit:
          medical?.latestVisit ||
          null,
      };
    }, [
      patientUser,
      localPatient,
      canonicalPatientUserId,
      medical,
      latestBmi,
    ]);

  /* =======================================================
     EMERGENCY
  ======================================================= */

  const emergencyData =
    useMemo(() => {
      const contact =
        emergencyContacts?.[
          0
        ];

      if (contact) {
        return {
          name:
            contact.name ||
            "—",

          phone:
            contact.phone ||
            "—",

          relation:
            contact.relation ||
            null,

          allContacts:
            emergencyContacts,
        };
      }

      return (
        patientRecordDashboardFallbacks
          ?.emergency ||
        DEFAULT_EMERGENCY
      );
    }, [
      emergencyContacts,
    ]);

  /* =======================================================
     INSURANCE
  ======================================================= */

  const insuranceData =
    useMemo(() => {
      const collectProviders = (value) => {
        if (Array.isArray(value)) {
          return value.flatMap((item) => collectProviders(item));
        }

        if (typeof value === "string") {
          const text = value.trim();
          if (!text) return [];

          try {
            const parsed = JSON.parse(text);
            return Array.isArray(parsed) ? collectProviders(parsed) : [text];
          } catch {
            return [text];
          }
        }

        if (value && typeof value === "object") {
          return Object.values(value).flatMap((item) => collectProviders(item));
        }

        return [];
      };

      const providers = collectProviders(medical?.insurance);

      if (
        medical?.nomMutuelle &&
        !providers.includes(
          medical.nomMutuelle
        )
      ) {
        providers.push(
          medical.nomMutuelle
        );
      }

      return {
        provider:
          providers
            .filter(Boolean)
            .join(", ") ||
          null,
      };
    }, [
      medical,
    ]);

  /*
   * utilisés ailleurs dans ton flux médical,
   * conservés pour que les fetch restent actifs.
   */
  void personalHistory;
  void vaccinations;
  void surgicalHistory;
  void familyHistory;

  /* =======================================================
     CALLBACKS
  ======================================================= */

  const handleConsultationSuccess =
    useCallback(
      async () =>
        await refreshConsultations(),
      [
        refreshConsultations,
      ]
    );

  const handleShareSuccess =
    useCallback(
      (documents) => {
        if (
          !Array.isArray(
            documents
          )
        ) {
          return;
        }

        setSharedFilesForCard(
          (previous) => {
            const ids =
              new Set(
                previous.map(
                  (item) =>
                    String(
                      item.id ||
                        item._id ||
                        ""
                    )
                )
              );

            return [
              ...previous,

              ...documents.filter(
                (item) =>
                  !ids.has(
                    String(
                      item.id ||
                        item._id ||
                        ""
                    )
                  )
              ),
            ];
          }
        );
      },
      []
    );

  const handleOpenPrescribePopup =
    useCallback(
      (
        consultationId,
        draft
      ) => {
        const id =
          normalizeId(
            consultationId
          );

        console.log(
          "➡️ OPEN POPUP",
          {
            consultationId:
              id,

            draft,
          }
        );

        setCreatedConsultationId(
          id
        );

        setConsultationDocumentDraft(
          draft ||
          null
        );

        setIsConsultationPopupOpen(
          true
        );
      },
      []
    );

  const handleClosePopup =
    useCallback(() => {
      setIsConsultationPopupOpen(
        false
      );

      setCreatedConsultationId(
        null
      );

      setConsultationDocumentDraft(
        null
      );
    }, []);

  const handlePopupSaved =
    useCallback(
      async () => {
        await refreshConsultations();
      },
      [
        refreshConsultations,
      ]
    );

  const handlePrintRecordFromPopup =
    useCallback(
      (
        document
      ) => {
        console.log(
          "PRINT:",
          document
        );
      },
      []
    );

  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <AppLayoutDoctor
      key={
        location.pathname
      }
    >
      <div className="file-page">
        {medicalAccessLoading ? (
          <div className="file-access-gate" role="status">
            <div className="file-access-gate__card">Checking patient authorization…</div>
          </div>
        ) : !canReadMedicalInformation ? (
          <section className="file-access-gate">
            <div className="file-access-gate__card">
              <h2>Patient authorization required</h2>
              <p>
                Medical information and consultation details are hidden until the patient grants access.
              </p>
              {medicalAccessStatus === "pending" ? (
                <>
                  <Alert
                    type="info"
                    showIcon
                    message="Authorization request pending"
                    description="The patient must approve your request before the file can be viewed."
                  />
                  <Button
                    className="file-access-gate__request-button"
                    type="primary"
                    size="large"
                    onClick={() => setAuthorizationDialogOpen(true)}
                    disabled={!patientScope.principalUserId || !doctorId}
                  >
                    Ask Authorization
                  </Button>
                </>
              ) : (
                <Button
                  type="primary"
                  size="large"
                  onClick={() => setAuthorizationDialogOpen(true)}
                  disabled={!patientScope.principalUserId || !doctorId}
                >
                  Ask Authorization
                </Button>
              )}
            </div>
            <Modal
              open={authorizationDialogOpen}
              title="Request patient authorization"
              okText="Send request"
              cancelText="Cancel"
              okButtonProps={{
                disabled: requestedAccessScopes.length === 0,
                loading: requestingAccess,
              }}
              cancelButtonProps={{ disabled: requestingAccess }}
              closable={!requestingAccess}
              maskClosable={!requestingAccess}
              onCancel={() => setAuthorizationDialogOpen(false)}
              onOk={handleRequestMedicalAccess}
            >
              <Alert
                type="info"
                showIcon
                message="The patient will review and approve the requested permissions."
                style={{ marginBottom: 16 }}
              />
              <Checkbox.Group
                value={requestedAccessScopes}
                onChange={setRequestedAccessScopes}
                style={{ display: "grid", gap: 10 }}
              >
                {MEDICAL_ACCESS_SCOPE_OPTIONS.map((option) => (
                  <Checkbox key={option.value} value={option.value}>
                    {option.label}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </Modal>
          </section>
        ) : (
          <>
        {!canCreateMedicalInformation && (
          <Alert
            type="warning"
            showIcon
            message="Patient approval is required to add consultations"
            description="You can view the medical file, but the patient must grant permission to add medical information before a consultation can be saved."
            action={(
              <Button
                size="small"
                loading={requestingAccess}
                disabled={!patientScope.principalUserId || !doctorId}
                onClick={handleRequestConsultationWriteAccess}
              >
                Request permission
              </Button>
            )}
            style={{ margin: "16px 24px" }}
          />
        )}
        <div className={`file-page-content${isNephrologyProfileOpen ? " file-page-content--with-panel" : ""}`}>
          <div className="file-left">
            <PatientProfileCard
              patientData={
                patientData
              }
              medicalAccessCapabilities={medicalAccessCapabilities}
              medicalAccessStatus={medicalAccessStatus}
              medicalAccessLoading={medicalAccessLoading}
              onOpenNephrologyProfile={() => setIsNephrologyProfileOpen(true)}
            />
          </div>

          {isNephrologyProfileOpen && (
            <NephrologyProfilePanel
              patient={patientData}
              onClose={() => setIsNephrologyProfileOpen(false)}
            />
          )}

          <div className="file-right">
            <div className="file-specialty-switcher">
              <div className="file-specialty-options" role="group" aria-label={t("specialty.label")}>
                {[
                  ["general", t("specialty.general")],
                  ["nephrologue", t("specialty.nephrology")],
                  ["pulmonology", t("specialty.pulmonology")],
                ].map(([specialty, label]) => (
                  <button
                    type="button"
                    key={specialty}
                    className={`file-specialty-option${selectedSpecialty === specialty ? " is-active" : ""}`}
                    aria-pressed={selectedSpecialty === specialty}
                    onClick={() => {
                      setSelectedSpecialty(specialty);
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {selectedSpecialty === "pulmonology" ? (
              <PulmonologyConsultationPage
                patientId={canonicalPatientUserId}
                onContinueToStep2={(initialDocumentKey) => handleOpenPrescribePopup(null, { frontOnly: true, initialDocumentKey })}
                onCancelConsultation={() => setSelectedSpecialty("general")}
              />
            ) : selectedSpecialty === "nephrologue" ? (
              <NephrologueConsultationPage
                userId={canonicalPatientUserId}
                medicalInfo={medical}
                patientMeasurements={{
                  height: patientData.height,
                  weight: patientData.weight,
                }}
                doctorId={doctorId}
                subprofileId={patientScope.subprofileId}
                onSuccess={handleConsultationSuccess}
                onOpenPrescribePopup={handleOpenPrescribePopup}
              />
            ) : (
              <ConsultationTabs
                userId={
                  canonicalPatientUserId
                }
                medicalInfo={medical}
                patientMeasurements={{
                  height: patientData.height,
                  weight: patientData.weight,
                }}
                patientInfo={{
                  birthdate: patientData.birthdate,
                  gender: patientData.gender,
                }}

                doctorId={
                  doctorId
                }

                consultations={
                  consultationsForHistory
                }

                sharedFiles={
                  sharedFilesForCard
                }

                onSuccess={
                  handleConsultationSuccess
                }

                onShareSuccess={
                  handleShareSuccess
                }

                onOpenPrescribePopup={
                  handleOpenPrescribePopup
                }
                specialty={selectedSpecialty}
                onCancelConsultation={() => setSelectedSpecialty("general")}
              />
            )}
          </div>
        </div>

        <AddNewConsultationPopup
          isOpen={
            isConsultationPopupOpen
          }

          onClose={
            handleClosePopup
          }

          onSaved={
            handlePopupSaved
          }

          consultationId={
            createdConsultationId
          }

          patientId={
            canonicalPatientUserId
          }

          frontOnly={
            consultationDocumentDraft
              ?.frontOnly ===
            true
          }

          initialDiagnosticText={
            consultationDocumentDraft
              ?.diagnosticText ||
            ""
          }

          initialDocumentTexts={
            consultationDocumentDraft
              ?.documentTexts ||
            null
          }

          initialDocumentKey={
            consultationDocumentDraft
              ?.initialDocumentKey ||
            null
          }

          initialSavedDocuments={
            consultationDocumentDraft
              ?.savedDocuments ||
            null
          }

          patientData={
            patientData
          }

          doctorId={
            doctorId
          }

          insuranceData={
            insuranceData
          }

          emergencyData={
            emergencyData
          }

          onPrintRecord={
            handlePrintRecordFromPopup
          }
        />
          </>
        )}
      </div>
    </AppLayoutDoctor>
  );
}
